import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { downloadPdf, apiError } from "@/lib/api";
import { preparePrint, resolveChannel } from "@/lib/printLabel";
import { toast } from "sonner";
import PaletteLabelPreview from "@/components/PaletteLabelPreview";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, PlusCircle, FileDown, Pencil, Lock, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GESTION, homeFor } from "@/lib/roles";

const LABEL_PAGE_CSS = `@media print { @page { size: 62mm 100mm; margin: 2mm; } .label-62x100 { transform: none !important; border: none !important; } }`;

export default function EtiquettePalette() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [palette, setPalette] = useState(null);
  const [settings, setSettings] = useState({ print_mode: "auto", copies: 1 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/palettes/${id}`).then((r) => setPalette(r.data));
    api.get("/settings").then((r) => setSettings({ print_mode: r.data.print_mode, copies: r.data.copies }));
  }, [id]);

  const pdf = async () => {
    setBusy(true);
    try {
      await downloadPdf(`/palettes/${id}/etiquette.pdf`, `etiquette_${palette.code_qr}.pdf`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const print = async () => {
    const job = preparePrint(settings);
    setBusy(true);
    try {
      const r = await api.get(`/palettes/${id}/pdf-url`);
      job.print(r.data.pdf_url);
      toast.success(job.channel === "brother" ? "Envoi vers Brother iPrint&Label…" : "PDF ouvert pour impression");
    } catch (err) {
      job.cancel();
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!palette)
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Chargement…</div>;

  const locked = palette.statut === "expediee";
  const channel = resolveChannel(settings.print_mode);

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <style>{LABEL_PAGE_CSS}</style>
      <div className="max-w-3xl mx-auto">
        <div className="no-print flex flex-wrap gap-3 mb-6 items-center justify-between">
          <Button variant="outline" onClick={() => navigate(GESTION.includes(user?.role) ? "/palettes" : homeFor(user?.role))} className="h-12 font-bold">
            <ArrowLeft className="w-5 h-5 mr-1" /> Retour
          </Button>
          <div className="flex flex-wrap gap-3">
            {GESTION.includes(user?.role) && (
              <Button
                data-testid="palette-edit-btn"
                variant="outline"
                onClick={() => navigate(`/palette/${id}/modifier`)}
                className={`h-12 font-bold ${locked ? "border-amber-300 text-amber-700" : ""}`}
              >
                {locked ? <Lock className="w-5 h-5 mr-1" /> : <Pencil className="w-5 h-5 mr-1" />}
                {locked ? "Expédiée" : "Rectifier"}
              </Button>
            )}
            <Button data-testid="production-jour-btn" variant="outline" onClick={() => navigate("/production")} className="h-12 font-bold">
              <ClipboardList className="w-5 h-5 mr-1" /> Production du jour
            </Button>
            <Button data-testid="new-palette-again-btn" variant="outline" onClick={() => navigate("/nouvelle-palette")} className="h-12 font-bold">
              <PlusCircle className="w-5 h-5 mr-1" /> Palette suivante
            </Button>
            <Button data-testid="palette-label-pdf-btn" variant="outline" onClick={pdf} disabled={busy} className="h-12 font-bold">
              <FileDown className="w-5 h-5 mr-1" /> PDF
            </Button>
            <Button data-testid="palette-label-print-btn" onClick={print} disabled={busy} className="h-12 font-bold bg-slate-900 hover:bg-slate-800">
              <Printer className="w-5 h-5 mr-1" /> Imprimer l'étiquette
            </Button>
          </div>
        </div>

        <div className="no-print mb-4 flex flex-wrap gap-2 items-center text-xs font-bold uppercase tracking-wide">
          <span data-testid="print-channel-badge" className="px-3 py-1.5 rounded-lg bg-slate-900 text-white">
            {channel === "brother" ? "Brother QL-820NWBc · iPrint&Label" : "Impression système (PDF)"}
          </span>
          <span className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600">
            62 × 100 mm · {settings.copies} copie{settings.copies > 1 ? "s" : ""}
          </span>
          {palette.modifications?.length > 0 && (
            <span data-testid="palette-modified-notice" className="px-3 py-1.5 rounded-lg text-amber-700 bg-amber-50 border border-amber-200">
              Rectifiée {palette.modifications.length} fois — réimprimez l'étiquette
            </span>
          )}
        </div>

        <div className="print-page" style={{ height: "calc(100mm * 1.6)" }}>
          <PaletteLabelPreview palette={palette} scale={1.6} />
        </div>
      </div>
    </div>
  );
}
